import * as React from "react";
import { Info } from "lucide-react";
import { Button } from "../ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../ui/popover";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "../ui/hover-card";
import { ScrollArea } from "../ui/scroll-area";
import { RecordingPlayer } from "../recording/RecordingPlayer";
import { RecordingData } from "../../hooks/useMediaRecorder";

// Type for additional info data - supports both legacy string and new object format
interface AdditionalInfoData {
  text: string;
  recordings?: {
    audio?: RecordingData;
    video?: RecordingData;
  };
}

interface ActivityNotesPopoverProps {
  htmlContent: string | AdditionalInfoData;
  activityLabel?: string;
}

// Helper to extract text from htmlContent (handles both string and object formats)
const getTextContent = (content: string | AdditionalInfoData | undefined): string => {
  if (!content) return "";
  if (typeof content === "string") return content;
  return content.text || "";
};

// Helper to extract recordings from htmlContent
const getRecordings = (content: string | AdditionalInfoData | undefined) => {
  if (!content || typeof content === "string") return null;
  return content.recordings || null;
};

export function ActivityNotesPopover({
  htmlContent,
}: ActivityNotesPopoverProps) {
  const [clickOpen, setClickOpen] = React.useState(false);

  const textContent = getTextContent(htmlContent);
  const recordings = getRecordings(htmlContent);
  const hasRecordings = !!(recordings?.audio || recordings?.video);

  // Check if content is not empty (strip HTML tags to check)
  const hasContent = React.useMemo(() => {
    if (!textContent && !hasRecordings) return false;
    if (hasRecordings) return true;
    const stripped = textContent.replace(/<[^>]*>/g, "").trim();
    return stripped.length > 0;
  }, [textContent, hasRecordings]);

  if (!hasContent) {
    return null;
  }

  const hasTextContent = textContent && textContent.replace(/<[^>]*>/g, "").trim().length > 0;

  return (
    <Popover open={clickOpen} onOpenChange={setClickOpen}>
      <HoverCard openDelay={200} closeDelay={100}>
        <PopoverTrigger asChild>
          <HoverCardTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 text-white hover:bg-white/20"
            >
              <Info className="h-4 w-4" />
            </Button>
          </HoverCardTrigger>
        </PopoverTrigger>

        {/* Hover Preview - Shows on hover */}
        <HoverCardContent
          className="w-96 p-0"
          align="end"
          sideOffset={8}
          side="bottom"
        >
          <ScrollArea className="max-h-[300px]">
            <div className="p-4 space-y-3">
              {hasTextContent && (
                <div
                  className="prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: textContent }}
                />
              )}
              {hasRecordings && (
                <div className="space-y-2">
                  {recordings?.audio && (
                    <RecordingPlayer
                      recording={recordings.audio}
                      type="audio"
                      compact
                      readOnly
                    />
                  )}
                  {recordings?.video && (
                    <RecordingPlayer
                      recording={recordings.video}
                      type="video"
                      compact
                      readOnly
                    />
                  )}
                </div>
              )}
            </div>
          </ScrollArea>
        </HoverCardContent>
      </HoverCard>

      {/* Click Popover - Opens on click and stays open */}
      <PopoverContent
        className="w-96 p-0"
        align="end"
        sideOffset={8}
        side="bottom"
      >
        <ScrollArea className="max-h-[400px]">
          <div className="p-4 space-y-3">
            {hasTextContent && (
              <div
                className="prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: textContent }}
              />
            )}
            {hasRecordings && (
              <div className="space-y-2">
                {recordings?.audio && (
                  <RecordingPlayer
                    recording={recordings.audio}
                    type="audio"
                    compact
                    readOnly
                  />
                )}
                {recordings?.video && (
                  <RecordingPlayer
                    recording={recordings.video}
                    type="video"
                    compact
                    readOnly
                  />
                )}
              </div>
            )}
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
